/**
 * Desktop (web push) notifications for this device.
 *
 * Redesign phase 1 (docs/fulcrum-redesign.md): this used to be a full-width
 * ~90px banner rendered on every page. The subscription logic is unchanged and
 * now lives in `PushNotificationsProvider`; presentation is split into:
 *  - `DesktopNotificationsMenuSection` — the enable/test/disable row inside the
 *    bell menu (NotificationCenter);
 *  - `DesktopNotificationsAlertBar` — a slim 40px inline bar, rendered ONLY when
 *    this device's subscription is broken (a personal-action alert);
 *  - `NotificationsDeniedHint` — unchanged, used on the Incidents page.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Bell, BellOff, RefreshCw, Send, X } from 'lucide-react';
import { Button } from '../ui/Button';
import {
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  type SubscribeFailureReason,
  type SubscribeResult,
} from '../../utils/serviceWorker';
import { jsonAuthHeaders } from '../../utils/authHeaders';
import { runtimeConfig } from '../../config/runtime';
import { cn } from '../../utils/cn';

type PermissionState = NotificationPermission | 'unsupported';
type SyncFailure = { reason: SubscribeFailureReason; detail?: string };
type SyncState =
  | { kind: 'idle' }
  | { kind: 'syncing' }
  | { kind: 'synced' }
  | { kind: 'error'; failure: SyncFailure };
type TestStatus = 'idle' | 'sending' | 'sent' | 'failed';

const readPermission = (): PermissionState => {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

const getExistingSubscription = async (): Promise<PushSubscription | null> => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
};

const stateFromResult = (result: SubscribeResult): SyncState =>
  result.ok
    ? { kind: 'synced' }
    : result.reason === 'no-subscription'
      ? { kind: 'idle' }
      : { kind: 'error', failure: { reason: result.reason, detail: result.detail } };

const failureCopy = (failure: SyncFailure): { title: string; hint?: string; showResetButton: boolean } => {
  switch (failure.reason) {
    case 'vapid':
      return {
        title: "Server isn't configured for push notifications.",
        hint: 'VAPID keys are missing on the backend — contact an admin.',
        showResetButton: false,
      };
    case 'backend-register':
      return {
        title: 'Server rejected the subscription.',
        hint: `${failure.detail || 'Unknown server error'}. Try logging out and back in if your session is stale.`,
        showResetButton: false,
      };
    case 'browser-subscribe':
      return {
        title: 'The browser refused to register this device for push.',
        hint: `${failure.detail || 'Unknown browser error'}. On iOS this usually means the app is not installed yet — open Share → "Add to Home Screen", then launch from the home icon. Otherwise click "Reset push" to clear stale state and try again.`,
        showResetButton: true,
      };
    case 'permission':
      return {
        title: 'Notification permission was not granted.',
        hint: 'Enable notifications for this site in your browser settings, then click Retry.',
        showResetButton: false,
      };
    case 'no-subscription':
      return {
        title: 'No subscription on this device.',
        hint: 'Open the notifications menu and click Enable to subscribe this device.',
        showResetButton: false,
      };
    case 'unsupported':
      return {
        title: 'This browser does not support push notifications.',
        hint: failure.detail,
        showResetButton: false,
      };
  }
};

const resetPushAndReload = async () => {
  try {
    await unsubscribeFromPushNotifications();
  } catch {
    /* ignore */
  }
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) await reg.unregister();
    }
  } catch {
    /* ignore */
  }
  window.location.reload();
};

interface PushNotificationsValue {
  permission: PermissionState;
  /** Permission granted AND this device holds a push subscription. */
  isActive: boolean;
  syncState: SyncState;
  busy: boolean;
  testStatus: TestStatus;
  enable: () => Promise<void>;
  retry: () => Promise<void>;
  disable: () => Promise<void>;
  sendTest: () => Promise<void>;
}

const PushNotificationsContext = createContext<PushNotificationsValue | null>(null);

/** Null outside the provider, so consumers degrade to rendering nothing. */
const usePushNotifications = (): PushNotificationsValue | null => useContext(PushNotificationsContext);

/**
 * Owns this device's push-subscription state. Mounted ONCE (Layout) so the
 * background self-heal sync runs once, however many places present the state.
 */
export const PushNotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permission, setPermission] = useState<PermissionState>('default');
  const [hasSubscription, setHasSubscription] = useState(false);
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState>({ kind: 'idle' });
  const [busy, setBusy] = useState(false);
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const initial = readPermission();
      if (cancelled) return;
      setPermission(initial);

      if (initial !== 'granted') return;

      const sub = await getExistingSubscription();
      if (cancelled) return;

      if (!sub) {
        // Permission is granted but the device has no subscription — happens
        // after the user clicked Disable. Don't silently re-subscribe; let them
        // click Enable explicitly.
        setHasSubscription(false);
        setEndpoint(null);
        return;
      }

      setHasSubscription(true);
      setEndpoint(sub.endpoint);

      // Self-heal DB-level drift: re-register the existing subscription with
      // the backend (don't create a new one if missing).
      setSyncState({ kind: 'syncing' });
      const result = await subscribeToPushNotifications({ createIfMissing: false });
      if (cancelled) return;
      if (!result.ok && result.reason !== 'no-subscription') {
        console.warn('[push] background sync failed:', result.reason, result.detail);
      }
      setSyncState(stateFromResult(result));
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      const result = await subscribeToPushNotifications({ createIfMissing: true });
      const next = readPermission();
      setPermission(next);
      if (result.ok) {
        setHasSubscription(true);
        setEndpoint(result.subscription.endpoint);
        setSyncState({ kind: 'synced' });
      } else if (next === 'granted') {
        setSyncState({ kind: 'error', failure: { reason: result.reason, detail: result.detail } });
      } else {
        setSyncState({ kind: 'idle' });
      }
    } finally {
      setBusy(false);
    }
  }, []);

  const retry = useCallback(async () => {
    setSyncState({ kind: 'syncing' });
    const result = await subscribeToPushNotifications({ createIfMissing: false });
    setSyncState(stateFromResult(result));
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      await unsubscribeFromPushNotifications();
      setHasSubscription(false);
      setEndpoint(null);
      setSyncState({ kind: 'idle' });
    } finally {
      setBusy(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    setTestStatus('sending');
    try {
      const res = await fetch(`${runtimeConfig.apiUrl}/api/push/test`, {
        method: 'POST',
        headers: jsonAuthHeaders(),
        credentials: 'include',
        // Scoped to this device's endpoint so a test from desktop doesn't also
        // wake the user's phone (and vice versa). Backend falls back to all
        // subs if the field is missing.
        body: JSON.stringify({ endpoint }),
      });
      setTestStatus(res.ok ? 'sent' : 'failed');
    } catch {
      setTestStatus('failed');
    } finally {
      setTimeout(() => setTestStatus('idle'), 4000);
    }
  }, [endpoint]);

  const value = useMemo<PushNotificationsValue>(() => ({
    permission,
    isActive: permission === 'granted' && hasSubscription,
    syncState,
    busy,
    testStatus,
    enable,
    retry,
    disable,
    sendTest,
  }), [permission, hasSubscription, syncState, busy, testStatus, enable, retry, disable, sendTest]);

  return <PushNotificationsContext.Provider value={value}>{children}</PushNotificationsContext.Provider>;
};

const TEST_LABEL: Record<TestStatus, string> = {
  idle: 'Send test',
  sending: 'Sending…',
  sent: 'Sent',
  failed: 'Failed',
};

const linkButton = 'text-xs font-medium underline-offset-2 hover:underline disabled:opacity-60';

/** Enable / status / test / disable row at the top of the bell menu. */
export const DesktopNotificationsMenuSection: React.FC = () => {
  const push = usePushNotifications();
  const [confirmDisable, setConfirmDisable] = useState(false);

  if (!push || push.permission === 'unsupported') return null;

  if (push.permission === 'denied') {
    return (
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-xs text-slate-500">
        <BellOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Desktop notifications are blocked in this browser&apos;s site settings.
      </div>
    );
  }

  if (!push.isActive) {
    // Permission may already be granted when this device was disabled earlier.
    const reEnable = push.permission === 'granted';
    return (
      <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-600">
          <Bell className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900">
            {reEnable ? 'Re-enable desktop notifications' : 'Enable desktop notifications'}
          </p>
          <p className="text-xs text-slate-500">
            {reEnable
              ? 'Allowed in your browser, but this device is not subscribed.'
              : 'Get incident alerts even when this tab is closed.'}
          </p>
        </div>
        <Button size="sm" onClick={() => void push.enable()} disabled={push.busy} className="shrink-0">
          {push.busy ? 'Enabling…' : 'Enable'}
        </Button>
      </div>
    );
  }

  if (confirmDisable) {
    return (
      <div className="flex items-center gap-3 border-b border-slate-100 bg-amber-50 px-4 py-2.5">
        <p className="flex-1 text-xs text-amber-800">Stop desktop notifications on this device?</p>
        <button type="button" className={cn(linkButton, 'text-slate-600')} onClick={() => setConfirmDisable(false)} disabled={push.busy}>
          Cancel
        </button>
        <button
          type="button"
          className={cn(linkButton, 'text-amber-800')}
          onClick={async () => { await push.disable(); setConfirmDisable(false); }}
          disabled={push.busy}
        >
          {push.busy ? 'Disabling…' : 'Disable'}
        </button>
      </div>
    );
  }

  if (push.syncState.kind === 'error') {
    const copy = failureCopy(push.syncState.failure);
    return (
      <div className="flex items-start gap-2.5 border-b border-slate-100 bg-amber-50 px-4 py-2.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-amber-900">{copy.title}</p>
          {copy.hint && <p className="mt-0.5 text-xs text-amber-800">{copy.hint}</p>}
          <div className="mt-1.5 flex items-center gap-3 text-amber-800">
            <button type="button" className={cn(linkButton, 'inline-flex items-center gap-1')} onClick={() => void push.retry()}>
              <RefreshCw className="h-3 w-3" aria-hidden="true" />
              Retry
            </button>
            {copy.showResetButton && (
              <button type="button" className={linkButton} onClick={() => void resetPushAndReload()}>
                Reset push
              </button>
            )}
            <button type="button" className={linkButton} onClick={() => setConfirmDisable(true)}>
              Disable
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-2.5">
      <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden="true" />
      <p className="flex-1 text-xs text-slate-600">
        Desktop notifications are on for this device
        {push.syncState.kind === 'syncing' && <span className="ml-1 text-slate-400">(syncing…)</span>}
      </p>
      <button
        type="button"
        className={cn(linkButton, 'inline-flex items-center gap-1 text-slate-600')}
        onClick={() => void push.sendTest()}
        disabled={push.testStatus === 'sending'}
      >
        <Send className="h-3 w-3" aria-hidden="true" />
        {TEST_LABEL[push.testStatus]}
      </button>
      <button type="button" className={cn(linkButton, 'text-slate-600')} onClick={() => setConfirmDisable(true)}>
        Disable
      </button>
    </div>
  );
};

const ALERT_DISMISS_KEY = 'desktop-notifications-alert-dismissed';

const readDismissedReason = (): string | null => {
  try {
    return sessionStorage.getItem(ALERT_DISMISS_KEY);
  } catch {
    return null;
  }
};

/**
 * Slim inline bar (h-10) shown above page content ONLY when this device's push
 * subscription is broken. Dismissal lasts for the browser session and is keyed
 * by failure reason, so a different failure surfaces again.
 */
export const DesktopNotificationsAlertBar: React.FC = () => {
  const push = usePushNotifications();
  const [dismissedReason, setDismissedReason] = useState<string | null>(readDismissedReason);

  if (!push || !push.isActive || push.syncState.kind !== 'error') return null;
  const { reason } = push.syncState.failure;
  if (dismissedReason === reason) return null;

  const copy = failureCopy(push.syncState.failure);
  const dismiss = () => {
    try {
      sessionStorage.setItem(ALERT_DISMISS_KEY, reason);
    } catch {
      // Ignore storage errors — the in-memory dismissal still applies.
    }
    setDismissedReason(reason);
  };

  return (
    <div
      role="alert"
      className="flex h-10 items-center gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 text-[13px] text-amber-800"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate" title={copy.hint}>
        <span className="font-semibold">Desktop notifications aren&apos;t working.</span> {copy.title}
      </span>
      <button type="button" className="shrink-0 font-medium underline" onClick={() => void push.retry()}>
        Retry
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 rounded p-0.5 text-amber-700 hover:bg-amber-100"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const NotificationsDeniedHint: React.FC = () => {
  const [permission, setPermission] = useState<PermissionState>('default');
  useEffect(() => setPermission(readPermission()), []);
  if (permission !== 'denied') return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center gap-2 text-sm text-amber-800">
      <BellOff size={16} />
      Notifications are blocked in this browser. Enable them in site settings to receive incident alerts.
    </div>
  );
};
